/**
 * @typedef {Object} DeliverablePlatform
 * @type {Object}
 * @property {String} key
 * @property {String} name
 *
 * @typedef {Object} DeliverableCurrency
 * @type {Object}
 * @property {String} key
 * @property {String} symbol
 * @property {String} name
 *
 * @typedef {Object} DeliverableObject
 * @type {Object}
 * @property {Number} id
 * @property {DeliverablePlatform} platform
 * @property {String} contentType
 * @property {String?} reviewDate
 * @property {String?} publishDate
 * @property {Number} count
 * @property {Number=} agreedFeeTotal
 * @property {Boolean=} isDefaultDeliverable
 * @property {DeliverableCurrency} currency
 */

import currencyUtil from '@/utils/currency';
import numberUtil from '@/utils/numbers';

const labels = Object.freeze({
    photo: {
        singular: 'Photo',
        plural: 'Photos'
    },
    video: {
        singular: 'Video',
        plural: 'Videos'
    },
    text: {
        singular: 'Post',
        plural: 'Posts'
    },
    story: {
        singular: 'Story',
        plural: 'Stories'
    },
    stream: {
        singular: 'Stream',
        plural: 'Streams'
    }
});

export class Deliverable {
    /**
     * Create a Deliverable
     * @param {DeliverableObject} deliverable - The wrapped DeliverableObject
     */
    constructor(deliverable) {
        this.deliverable = deliverable;
    }

    /**
     * Get a clone, with selected properties overriden
     * @param {DeliverableObject} overrides - An object with overrides
     */
    getClone(overrides) {
        return new Deliverable({
            ...this.deliverable,
            ...overrides
        });
    }

    get isDefaultDeliverable() {
        // The BI API doesn't send isDefaultDeliverable when the deliverable is not a default one.
        // Hence, we assume it's false
        return this.deliverable?.isDefaultDeliverable ?? false;
    }
    get id() {
        return this.deliverable.id;
    }
    get platform() {
        return { ...this.deliverable.platform };
    }
    get contentType() {
        return this.deliverable.contentType;
    }
    get reviewDate() {
        return this.deliverable.reviewDate;
    }
    get publishDate() {
        return this.deliverable.publishDate;
    }
    get count() {
        return this.deliverable.count;
    }
    get agreedFeeTotal() {
        // The BI API doesn't send agreedFeeTotal when the deliverable is a default one.
        return this.deliverable?.agreedFeeTotal ?? null;
    }
    get currency() {
        return { ...this.deliverable.currency };
    }

    /**
     * Get the label for a given content type, accounting for singular/plural.
     * If type is unknown it will be returned as-is.
     * TODO: use i18n lib for this!
     * @param {Object}
     * @property {Boolean} displayPlatform - Whether to include the platform
     * @property {Boolean} displayCount - Whether to include the number of deliverable items
     * @returns {String}
     */
    getContentLabel({ displayPlatform = false, displayCount = true }) {
        if (!labels[this.deliverable.contentType]) {
            return this.deliverable.contentType;
        }
        return [
            displayCount ?  `${this.deliverable.count}` : '',
            displayPlatform ? this.deliverable.platform.name : '',
            Number(this.deliverable.count) === 1 ? labels[this.deliverable.contentType].singular : labels[this.deliverable.contentType].plural
        ].filter((word) => word && typeof word === 'string').join(' ');
    }

    /**
     * Checks if the agreedFeeTotal and currency exist
     * @returns {Boolean}
     */
    get hasContentFee() {
        if (this.deliverable.agreedFeeTotal === undefined || this.deliverable.agreedFeeTotal === null || isNaN(this.deliverable.agreedFeeTotal) || !this.deliverable.currency) {
            return false;
        }
        // If fee is 0 we will treat it as undefined (Current default value that BI uses when creating a deliverable)
        if (this.deliverable.agreedFeeTotal === 0) {
            return false;
        }

        return true;
    }

    /**
     * The abbreviated version of the value (e.g. $1M)
     * If the value is less than 1,000,000 it will not be abbreviated.
     * @returns {String}
     */
    get contentFee() {
        if (this.deliverable.agreedFeeTotal === undefined || this.deliverable.agreedFeeTotal === null || isNaN(this.deliverable.agreedFeeTotal) || !this.deliverable.currency) {
            return null;
        }

        if (this.deliverable.agreedFeeTotal < 1000000) {
            return currencyUtil.formatCurrencyDisplay(this.deliverable.agreedFeeTotal, this.deliverable.currency);
        }

        return `${this.deliverable.currency.symbol}${numberUtil.formatNumberDisplay(this.deliverable.agreedFeeTotal, 4)}`;
    }

    /**
     * Returns the full formatted currency
     * @returns {String}
     */
    get fullFormattedCurrency() {
        return currencyUtil.formatCurrencyDisplay(this.deliverable.agreedFeeTotal, this.deliverable.currency);
    }
}
